import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@bufferapp/ui/Icon/Icons/Search';
import Text from '@bufferapp/ui/Text';
import Loader from '@bufferapp/ui/Loader';

import { useLazyQuery } from '@apollo/client';
import { UNSPLASH_SERVICE } from '~/integrations-bar/constants';
import {
  SearchButtonContainer,
  SpanWithMarginLeft,
  LoaderWrapper,
  ButtonWihMarginLeft,
  InputSearchWrapper,
  StyledNotice,
  FooterForm,
  CleanIcon,
  InputSearch,
} from './styles';

import { useSearchImagesReducer, actions } from './searchImagesReducer';

import { SearchImagesResults } from './SearchImagesResults';
import { SEARCH_IMAGES_QUERY } from '../../queries';

import { QUERY_RUN, sendIntegrationsEvent } from '../../tracking';

import { IntegrationsService, IntegrationsContext } from '../../types';
import { OrientationDropdownMenu } from './OrientationDropdownMenu';

/**
 *  COMPONENT: SEARCH IMAGES FORM
 */
export const SearchImagesForm = ({
  // todo: rename editor to integrationsContext
  editor,
  service,
}: {
  editor: IntegrationsContext;
  service: IntegrationsService;
}) => {
  const { t } = useTranslation();
  const isMounted = useRef(false);
  const [inputValue, setInputValue] = useState('');
  const [state, dispatch] = useSearchImagesReducer();
  const [searchImages, { loading }] = useLazyQuery(SEARCH_IMAGES_QUERY);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  useEffect(() => {
    if (state.query && state.query !== '') {
      sendIntegrationsEvent(QUERY_RUN, editor, service.id, state.query);
    }
    searchImages({
      variables: {
        searchTerm: state.query,
        page: state.currentPage,
        orientation: state.orientation,
        source: service?.id,
      },
    }).then((result) => {
      if (isMounted.current) {
        const { error, data } = result;

        if (error || data?.searchImages?.userFriendlyMessage) {
          const msg = error?.message || data?.searchImages?.userFriendlyMessage;
          dispatch(actions.searchError(msg));
        } else {
          dispatch(actions.searchSucccess(data?.searchImages?.images));
        }
      }
    });
  }, [
    dispatch,
    searchImages,
    state.currentPage,
    state.query,
    editor,
    service,
    state.orientation,
  ]);

  const handleOrientationChange = (newOrientation) => {
    dispatch(actions.changeOrientation(newOrientation));
  };

  if (service === null) return null;

  return (
    <>
      {state.error && (
        <StyledNotice type="alert" disableAnimation>
          <Text>{state.error}</Text>
        </StyledNotice>
      )}
      <Text type="h3">
        {service?.icon?.({}) ?? null}
        <SpanWithMarginLeft>{service?.title}</SpanWithMarginLeft>
      </Text>
      <SearchButtonContainer>
        <InputSearchWrapper>
          <InputSearch
            type="text"
            onChange={(e) => setInputValue(e.target.value)}
            name="search-terms"
            placeholder={service?.placeholder}
            icon={<SearchIcon size="medium" />}
            size="tall"
            value={inputValue}
            ref={(input) => input && input.focus()}
          />
          {inputValue && (
            <CleanIcon
              title="Clean"
              data-testid="clean-icon"
              onClick={(e) => {
                e.preventDefault();
                setInputValue('');
                dispatch(actions.cleanQuery());
              }}
            />
          )}
        </InputSearchWrapper>
        <ButtonWihMarginLeft
          type="primary"
          aria-label="search"
          onClick={(e) => {
            e.preventDefault();
            dispatch(actions.search(inputValue));
          }}
          disable={loading}
          label={t('content.modals.integrations.searchButon')}
        />
      </SearchButtonContainer>
      {service.id === UNSPLASH_SERVICE && state.query && state.query !== '' && (
        <OrientationDropdownMenu
          onChangeOrientation={handleOrientationChange}
        />
      )}
      <SearchImagesResults
        images={state.images}
        getMore={() => dispatch(actions.getMore())}
        loading={loading}
        service={service}
        loader={
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        }
      />
      <FooterForm> {service?.footer && service.footer}</FooterForm>
    </>
  );
};
